import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import ToolCard from '@/components/ToolCard';
import { CATEGORIES } from '@/lib/tools';
import { safeJsonLd } from '@/lib/jsonLd';
import { MagnifyingGlass, Clock, Sparkle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RECENT_KEY = 'ff-recent-tools';

export default function Home() {
  const [tools, setTools] = useState([]);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/tools`);
        setTools(res.data.tools || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    try {
      const r = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      setRecent(r);
    } catch (e) {
      console.warn('Failed to read recent tools from localStorage:', e);
      setRecent([]);
    }
  }, []);

  const filtered = useMemo(() => {
    return tools.filter(t => {
      const matchCat = activeCat === 'all' || t.category === activeCat;
      const q = query.trim().toLowerCase();
      const matchQ = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [tools, query, activeCat]);

  const recentTools = recent
    .map(id => tools.find(t => t.id === id))
    .filter(Boolean)
    .slice(0, 6);

  return (
    <div className="min-h-screen">
      {/* ============ HERO — Modern SaaS ============ */}
      <section className="relative overflow-hidden">
        <div className="hero-halo absolute inset-0" aria-hidden="true" />
        {/* Sparkle backdrop dots */}
        <div aria-hidden="true">
          <span className="sparkle bg-blue-400" style={{ top: '15%', left: '8%' }} />
          <span className="sparkle bg-pink-400" style={{ top: '65%', left: '4%', animationDelay: '.8s' }} />
          <span className="sparkle bg-blue-300" style={{ top: '75%', right: '18%', animationDelay: '1.5s' }} />
          <span className="sparkle bg-pink-300" style={{ top: '20%', right: '30%', animationDelay: '2.2s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span data-testid="hero-badge" className="badge-soft">
              <Sparkle weight="fill" size={14} /> {tools.length || 'All'} tools · One workshop
            </span>
            <h1 className="font-display font-extrabold tracking-tight mt-6 text-5xl sm:text-6xl md:text-7xl leading-[1.02]" style={{ color: 'var(--ink)' }}>
              Any document.<br />
              Any format.<br />
              <span style={{ color: 'var(--brand)' }}>No fuss.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed font-body">
              Convert, merge, split, compress, e-sign and grammar-check your files
              with a free browser-based toolbox. Drag it in — download it back in seconds.
            </p>
            <div className="mt-9 flex items-center gap-3 flex-wrap">
              <a href="#all-tools" data-testid="hero-cta-primary" className="btn-primary">
                Browse tools
                <span aria-hidden="true">→</span>
              </a>
              <a href="/tools/smart-compress" data-testid="hero-cta-compress" className="btn-secondary">
                <span className="w-5 h-5 rounded-md grid place-items-center bg-blue-100 text-blue-700 text-xs">⚡</span>
                Smart compress
              </a>
              <a href="/tools/sign-pdf" data-testid="hero-cta-sign" className="btn-secondary">
                <span className="w-5 h-5 rounded-md grid place-items-center bg-pink-100 text-pink-700 text-xs">✎</span>
                Sign PDF
              </a>
            </div>
            <p className="mt-5 text-xs text-slate-500 font-mono-plex">
              Files auto-delete in 1 hour · No signup · No watermark
            </p>
          </div>

          {/* Right-hand file-card illustration */}
          <div className="md:col-span-5 relative min-h-[380px] md:min-h-[440px]" aria-hidden="true">
            <div className="absolute inset-0">
              {/* Soft rounded pastel plate behind the cards */}
              <div className="absolute top-6 right-6 w-72 h-72 md:w-80 md:h-80 rounded-[40%] bg-gradient-to-br from-blue-100 via-white to-pink-100 blur-2xl opacity-90" />
              <div className="file-card pdf"  style={{ top: '18%', left: '18%' }}>PDF</div>
              <div className="file-card word" style={{ top: '8%',  right: '18%' }}>W</div>
              <div className="file-card xls"  style={{ bottom: '20%', left: '8%' }}>XLSX</div>
              <div className="file-card ppt"  style={{ bottom: '10%', right: '10%' }}>PPT</div>
              {/* Dotted flight-path */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" fill="none">
                <path d="M60 120 Q 180 40 300 100 Q 380 200 260 300 Q 120 360 80 240" stroke="#c7d2fe" strokeWidth="2" strokeDasharray="4 6" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* RECENT */}
      {recentTools.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 mt-4">
          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="tile tile-blue" style={{ width: 32, height: 32, borderRadius: 10 }}>
                  <Clock weight="bold" size={16} />
                </span>
                <h2 className="font-display font-semibold text-lg">Recently used</h2>
              </div>
            </div>
            <div data-testid="recent-tools-row" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {recentTools.map(t => (
                <a
                  key={t.id}
                  href={`/tools/${t.id}`}
                  data-testid={`recent-tool-${t.id}`}
                  className="card-soft p-4 flex items-center gap-3 group"
                >
                  <span className={`tile ${CATEGORIES[t.category]?.tileClass || 'tile-blue'}`} style={{ width: 40, height: 40, borderRadius: 12 }}>
                    <span className="text-xs font-bold">{t.name.substring(0, 2).toUpperCase()}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{t.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{CATEGORIES[t.category]?.label}</p>
                  </div>
                  <span className="text-slate-300 group-hover:text-blue-500" style={{ transition: 'color .2s' }}>→</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TOOL GRID */}
      <section id="all-tools" className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 font-semibold">
              Toolbox
            </p>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-2" style={{ color: 'var(--ink)' }}>
              Pick your conversion
            </h2>
            <p className="text-slate-600 mt-2 max-w-lg">
              Convert documents quickly and accurately with the right tool for your needs.
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <MagnifyingGlass
              size={18}
              weight="bold"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              data-testid="tool-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools…"
              className="w-full pl-11 pr-4 py-3 border border-slate-200 bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-sm shadow-sm"
              style={{ transition: 'all 0.2s ease' }}
            />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mb-10">
          <button
            data-testid="filter-all"
            onClick={() => setActiveCat('all')}
            className={`chip-pill ${activeCat === 'all' ? 'active' : ''}`}
          >
            All ({tools.length})
          </button>
          {Object.values(CATEGORIES).map(c => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setActiveCat(c.id)}
              className={`chip-pill ${activeCat === c.id ? 'active' : ''}`}
            >
              {c.label} ({tools.filter(t => t.category === c.id).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-56 card-flat animate-pulse" />
            ))}
          </div>
        ) : (
          <div
            data-testid="tool-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filtered.map((t, i) => (
              <ToolCard key={t.id} tool={t} index={i} />
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div data-testid="tool-empty" className="card-flat p-12 text-center">
            <p className="font-display text-2xl mb-2">No tools match that search</p>
            <p className="text-slate-500 text-sm">Try clearing the filter or another keyword.</p>
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-3 gap-6">
          {[
            { n: '01', t: 'Pick a tool', d: 'Choose from 30 converters and PDF utilities — categorised for speed.' },
            { n: '02', t: 'Drop your file', d: 'Drag & drop or browse. Multi-file supported for merges and batch conversions.' },
            { n: '03', t: 'Download', d: 'Get the result instantly. Files auto-delete after 1 hour. No account needed.' },
          ].map((s, i) => (
            <div key={s.n} className="card-soft p-8 card-in" style={{ animationDelay: `${i * 80}ms` }}>
              <p className="font-mono-plex text-xs uppercase tracking-widest text-slate-400">Step {s.n}</p>
              <h3 className="font-display font-bold text-2xl mt-3" style={{ color: 'var(--ink)' }}>{s.t}</h3>
              <p className="text-slate-600 text-sm mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SEO — Popular conversions internal-link hub */}
      <section id="popular-conversions" className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <p className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 font-semibold">Popular</p>
        <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-2" style={{ color: 'var(--ink)' }}>
          Free online document conversions
        </h2>
        <p className="text-slate-600 mt-4 max-w-3xl leading-relaxed">
          Convert any file format to PDF, Word, Excel, PowerPoint, and more.
          Go Convert Documents is a fast, secure, free online document converter that works entirely in the cloud — no registration, no watermarks, no software to install.
          Every file is deleted from our servers within one hour.
        </p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { id: 'pdf-to-word', label: 'PDF to Word' },
            { id: 'word-to-pdf', label: 'Word to PDF' },
            { id: 'pdf-to-excel', label: 'PDF to Excel' },
            { id: 'excel-to-pdf', label: 'Excel to PDF' },
            { id: 'pdf-to-powerpoint', label: 'PDF to PPT' },
            { id: 'powerpoint-to-pdf', label: 'PPT to PDF' },
            { id: 'pdf-to-jpg', label: 'PDF to JPG' },
            { id: 'jpg-to-pdf', label: 'JPG to PDF' },
            { id: 'merge-pdf', label: 'Merge PDF' },
            { id: 'split-pdf', label: 'Split PDF' },
            { id: 'compress-pdf', label: 'Compress PDF' },
            { id: 'ocr-pdf', label: 'OCR PDF' },
          ].map((l) => (
            <a
              key={l.id}
              href={`/tools/${l.id}`}
              data-testid={`popular-link-${l.id}`}
              className="card-flat px-4 py-3 text-sm font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-600 flex items-center justify-between group"
              style={{ transition: 'all 0.2s ease' }}
            >
              {l.label}
              <span className="text-slate-300 group-hover:text-blue-500" style={{ transition: 'color .2s' }}>→</span>
            </a>
          ))}
        </div>
      </section>

      {/* SEO — Why choose section */}
      <section className="bg-gradient-to-b from-white to-blue-50/40 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-3 gap-8">
          {[
            { title: 'Fast & free', body: 'Every tool is 100% free with no daily limit. Most conversions finish in under 10 seconds thanks to server-side processing with LibreOffice, Ghostscript and Tesseract OCR.', tile: 'tile-blue', emoji: '⚡' },
            { title: 'Secure & private', body: 'Files travel over HTTPS and are auto-deleted from our servers after one hour. No account, no email, no tracking of your document contents.', tile: 'tile-pink', emoji: '🔒' },
            { title: 'Works everywhere', body: 'Runs in any modern browser on Windows, macOS, Linux, Android and iOS. Convert PDF, DOCX, XLSX, PPTX, TXT, JPG, PNG, WEBP, BMP and GIF from a single site.', tile: 'tile-green', emoji: '🌐' },
          ].map((b) => (
            <div key={b.title} className="card-soft p-8">
              <span className={`tile ${b.tile}`} style={{ width: 44, height: 44, borderRadius: 12, fontSize: 20 }}>{b.emoji}</span>
              <h3 className="font-display font-bold text-2xl mt-4" style={{ color: 'var(--ink)' }}>{b.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ (with JSON-LD for rich snippets) */}
      <section id="faq" className="max-w-4xl mx-auto px-5 md:px-8 py-20">
        <p className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 font-semibold">FAQ</p>
        <h2 className="font-display font-extrabold text-4xl md:text-5xl mt-2" style={{ color: 'var(--ink)' }}>
          Frequently asked questions
        </h2>

        <div className="mt-10 space-y-3">
          {[
            {
              q: 'Is Go Convert Documents free?',
              a: 'Yes, every tool on Go Convert Documents is 100% free with no registration required. Files up to 100 MB can be converted without any limit.',
            },
            {
              q: 'Do I need to sign up to convert files?',
              a: 'No. You can convert, merge, split, compress or protect your documents anonymously — no account, no email, no credit card.',
            },
            {
              q: 'How long are my files stored on the server?',
              a: 'Uploaded and converted files are automatically deleted after one hour. We do not analyze, share or index your document contents.',
            },
            {
              q: 'Which file formats are supported?',
              a: 'PDF, Microsoft Word (DOCX/DOC), Excel (XLSX/XLS), PowerPoint (PPTX/PPT), plain text (TXT) and image formats JPG, PNG, WEBP, BMP and GIF.',
            },
            {
              q: 'Can I convert scanned PDFs to editable text?',
              a: 'Yes. Use the OCR PDF tool — it runs Tesseract OCR across every page and returns clean editable text in English, Spanish, French, German, Portuguese or Italian.',
            },
            {
              q: 'Can I password-protect a PDF?',
              a: 'Yes. The Protect PDF tool encrypts your file with a password of your choice using 128-bit encryption. You can remove the password later with Unlock PDF.',
            },
          ].map((f, i) => (
            <details
              key={f.q}
              data-testid={`faq-item-${i}`}
              className="card-flat p-5"
            >
              <summary className="cursor-pointer font-semibold text-lg" style={{ color: 'var(--ink)' }}>
                {f.q}
              </summary>
              <p className="text-slate-700 mt-3 leading-relaxed text-sm">{f.a}</p>
            </details>
          ))}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                { '@type': 'Question', name: 'Is Go Convert Documents free?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, every tool on Go Convert Documents is 100% free with no registration required. Files up to 100 MB can be converted without any limit.' } },
                { '@type': 'Question', name: 'Do I need to sign up to convert files?', acceptedAnswer: { '@type': 'Answer', text: 'No. You can convert, merge, split, compress or protect your documents anonymously — no account, no email, no credit card.' } },
                { '@type': 'Question', name: 'How long are my files stored on the server?', acceptedAnswer: { '@type': 'Answer', text: 'Uploaded and converted files are automatically deleted after one hour. We do not analyze, share or index your document contents.' } },
                { '@type': 'Question', name: 'Which file formats are supported?', acceptedAnswer: { '@type': 'Answer', text: 'PDF, Microsoft Word (DOCX/DOC), Excel (XLSX/XLS), PowerPoint (PPTX/PPT), plain text (TXT) and image formats JPG, PNG, WEBP, BMP and GIF.' } },
                { '@type': 'Question', name: 'Can I convert scanned PDFs to editable text?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Use the OCR PDF tool — it runs Tesseract OCR across every page and returns clean editable text in English, Spanish, French, German, Portuguese or Italian.' } },
                { '@type': 'Question', name: 'Can I password-protect a PDF?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. The Protect PDF tool encrypts your file with a password of your choice using 128-bit encryption. You can remove the password later with Unlock PDF.' } },
              ],
            }),
          }}
        />
      </section>
    </div>
  );
}
